import {
  useCaptchaService,
  usePaymentService,
} from '@/layout/appWrapper/ServiceProvider';
import { SWRUser } from '@/services/userService';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { useCallback } from 'react';
import { KeyedMutator } from 'swr';
import { StripeSubscriptionStatus } from '@/services/paymentService';

type OfferPlan = 'weekly' | 'yearly' | 'welcome' | 'free';

const useStripeMethods = (
  stripe: Stripe,
  mutateUser: KeyedMutator<SWRUser>,
  isFreeTrialEnabled: boolean,
  introPriceId?: string,
  priceId = '',
  unlockedExports?: number,
  offerPlan?: OfferPlan,
  paywallVariant?: 'pcp' | 'ocp'
) => {
  const { validateCaptcha } = useCaptchaService();
  const { createStripeSubscription, getStripeClientIntentsData } =
    usePaymentService();

  const subscribe = useCallback(
    async ({
      elements,
      isEmailReminderEnabled,
      paymentStrategyId,
      willDownloadAutomatically,
    }: {
      elements: StripeElements;
      isEmailReminderEnabled: boolean;
      paymentStrategyId: string;
      willDownloadAutomatically: boolean;
    }): Promise<StripeSubscriptionStatus> => {
      const { clientSecret } = await getStripeClientIntentsData();

      await validateCaptcha();

      const redirectURL = new URL(document.URL);
      redirectURL.searchParams.append('priceId', priceId);
      redirectURL.searchParams.append(
        'freeTrialEnabled',
        String(isFreeTrialEnabled)
      );
      redirectURL.searchParams.append(
        'emailReminderEnabled',
        String(isEmailReminderEnabled)
      );
      redirectURL.searchParams.append(
        'withIntroPrice',
        String(Boolean(introPriceId))
      );
      redirectURL.searchParams.append('introPriceId', introPriceId || '');
      redirectURL.searchParams.append(
        'unlockedExports',
        String(unlockedExports)
      );
      redirectURL.searchParams.append('paymentStrategyId', paymentStrategyId);
      redirectURL.searchParams.append(
        'willDownloadAutomatically',
        String(willDownloadAutomatically)
      );
      if (offerPlan) redirectURL.searchParams.append('offerPlan', offerPlan);
      if (paywallVariant) {
        redirectURL.searchParams.append('paywallVariant', paywallVariant);
      }

      const response = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: redirectURL.toString(),
        },
        redirect: 'if_required',
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const result = await createStripeSubscription(
        response.setupIntent.payment_method as string,
        priceId,
        isFreeTrialEnabled,
        introPriceId,
        unlockedExports
      );
      await mutateUser();

      return result.status;
    },
    [
      createStripeSubscription,
      getStripeClientIntentsData,
      isFreeTrialEnabled,
      mutateUser,
      priceId,
      stripe,
      unlockedExports,
      validateCaptcha,
      introPriceId,
      offerPlan,
      paywallVariant,
    ]
  );

  return { subscribe };
};

export default useStripeMethods;
